import type { AppConfig, DeepPartial } from '@shared/app-config/types'
import { APP_API_URL, APP_AUTH_API_URL, APP_WEBSOCKET_URL } from '@shared/config/raw-env'
import globalEvents from '@shared/iframe-messages/global-events'
import { mergeConfig } from '@shared/util/merge-object'
import Cookies from 'js-cookie'
import { buildSchemaUrl } from './migration'

const AUTH_STORAGE_KEY = '@wippy-gen2/token'

const defaultAppConfig: AppConfig = Object.freeze({
  $schema: buildSchemaUrl(),
  auth: { token: '', expiresAt: '' },
  env: { APP_API_URL: '', APP_AUTH_API_URL: '', APP_WEBSOCKET_URL: '' },
  themeMode: 'auto',
  theming: {},
  hostConfig: {
    showAdmin: false,
    allowSelectModel: true,
    startNavOpen: true,
  },
  context: { resourceId: '', resourceType: 'page' },
} as AppConfig)

let appConfigStore: AppConfig = Object.freeze({ ...defaultAppConfig })

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b)
    return true
  if (a == null || b == null)
    return false
  if (typeof a !== typeof b)
    return false
  if (typeof a !== 'object')
    return false
  if (Array.isArray(a) !== Array.isArray(b))
    return false
  if (Array.isArray(a)) {
    if (a.length !== (b as unknown[]).length)
      return false
    return a.every((val, i) => deepEqual(val, (b as unknown[])[i]))
  }
  const keysA = Object.keys(a as Record<string, unknown>)
  const keysB = Object.keys(b as Record<string, unknown>)
  if (keysA.length !== keysB.length)
    return false
  for (const key of keysA) {
    if (!deepEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]))
      return false
  }
  return true
}

export function initAppConfig() {
  const url = new URL(window.location.href)
  const token = url.searchParams.get('token') || ''
  const expiresAt = url.searchParams.get('expiresAt') || ''
  const persist = url.searchParams.has('persist')
  const themeParam = url.searchParams.get('theme')
  const themeMode = themeParam === 'light' || themeParam === 'dark' || themeParam === 'auto'
    ? themeParam
    : undefined

  // Seed session.type from URL ?persist as a fallback. Later sources
  // (externalConfig / facade SetConfig) may override via updateAppConfig().
  // Cookie side-effects are NOT applied here — applySessionPolicy() runs
  // from resolveConfig() after all config sources have merged.
  const config: AppConfig = mergeConfig(defaultAppConfig, {
    auth: { token, expiresAt },
    env: { APP_API_URL, APP_AUTH_API_URL, APP_WEBSOCKET_URL },
    ...(themeMode ? { themeMode } : {}),
    hostConfig: {
      session: { type: persist ? 'cookie' : 'non-persistent' },
      history: 'hash',
    },
  })

  setAppConfig(config)
}

/**
 * Reads the final session.type from the current config and applies cookie
 * storage policy. Normalizes unknown values (anything not 'cookie') to
 * 'non-persistent'. Called from resolveConfig() after each merge point so
 * the facade's hostConfig.session.type can take effect after it arrives.
 */
export function applySessionPolicy() {
  const config = appConfigStore
  const rawType = config.hostConfig.session?.type
  const normalized: 'non-persistent' | 'cookie' = rawType === 'cookie' ? 'cookie' : 'non-persistent'

  // If backend sent an unknown value, correct it in the store so consumers
  // see the normalized value.
  if (rawType !== normalized) {
    updateAppConfig({
      hostConfig: { session: { type: normalized } },
    } as DeepPartial<AppConfig>)
  }

  if (normalized !== 'cookie')
    return

  const currentToken = appConfigStore.auth.token
  if (currentToken) {
    Cookies.set(AUTH_STORAGE_KEY, currentToken, { expires: 7 })
  }
  else {
    const stored = Cookies.get(AUTH_STORAGE_KEY)
    if (stored) {
      updateAppConfig({
        auth: { token: stored },
      } as DeepPartial<AppConfig>)
    }
  }
}

export function setAppConfig(config: AppConfig) {
  appConfigStore = Object.freeze({ ...config })
}

/**
 * Merges partial config into the current config.
 * Accepts either new AppConfig partial or raw PostMessage data (auto-migrated).
 * Emits 'configUpdated' on globalEvents if the config actually changed.
 */
export function updateAppConfig(config: DeepPartial<AppConfig>): boolean {
  // Pure merge — callers must pass current AppConfig format.
  // Migration from legacy/older schemas happens at the boundary (resolve.ts PostMessage listener).
  const prev = appConfigStore
  const next = Object.freeze(mergeConfig(appConfigStore, config))
  if (deepEqual(prev, next))
    return false
  appConfigStore = next
  globalEvents.emit('configUpdated')
  return true
}

export const getAppConfig = () => appConfigStore

// ─── Deprecated aliases ───
/** @deprecated Use getAppConfig() */
export const getChatConfig = getAppConfig
/** @deprecated Use setAppConfig() */
export const setChatConfig = setAppConfig
/** @deprecated Use updateAppConfig() */
export const updateChatConfig = updateAppConfig
/** @deprecated Use initAppConfig() */
export const initChatConfig = initAppConfig
